import { isEmpty } from "lodash";
import { getCompositeMeasuresObject } from "./composite";
import { MeasureSeriesSortingType, ReferenceDateDimension, VisitNodePassage } from "./types";
import { buildComparisonVisitor, buildCustomVisitor, buildDatapointsVisitor, buildMultiRequestVisitor, buildNodesVisitor } from "./visitor";
import { DatapointsVisitorEnum } from "./visitors/datapoints/datapointsVisitors";
import { NodesVisitorEnum } from "./visitors/nodes/nodesVisitors";
const buildVisitor = (condition, build) => condition && build?.();
export const buildPipeline = (config) => (dataList, request) => {
    const argsMultiRequest = { context: { config: config.multiRequestVisitorsReducer ?? [] }, visitable: dataList || [] };
    buildVisitor(config?.multiRequestVisitorsReducer, () => buildMultiRequestVisitor(config)(argsMultiRequest));
    const returnValue = [];
    const visitorArgs = [];
    argsMultiRequest.visitable.forEach((data, index) => {
        const args = { context: { request }, visitable: data.series || [] };
        buildVisitor(config?.datapointsVisitorsReducer, () => buildDatapointsVisitor(config)(args));
        if (config?.compositeObjectBuilder) {
            const { measures, dimensions: nullableDimensions, options } = config.compositeObjectBuilder, dimensions = !nullableDimensions || isEmpty(nullableDimensions) ? [ReferenceDateDimension] : nullableDimensions, totals = (data.totals ?? []);
            visitorArgs.push({
                context: { componentProps: config.componentProps,
                    datapointsMetadata: config.datapointsMetadata,
                    request,
                    staticData: { dimensions,
                        root: [],
                        totals },
                    visitNodePassage: VisitNodePassage.Always,
                    visitedDimensions: {},
                    widgetState: config.widgetState },
                visitable: {}
            });
            visitorArgs[index].context.staticData.root = getCompositeMeasuresObject(args.visitable, {}, measures, dimensions, data.totals
                ? [...data.totals]
                : [], { ...options, isSprout: false });
            buildVisitor(config?.nodesVisitorsReducer, () => buildNodesVisitor(config?.nodesVisitorsReducer)(visitorArgs[index]));
            buildVisitor(config?.customVisitorsReducer, () => buildCustomVisitor(config?.customVisitorsReducer)(visitorArgs[index]));
            returnValue.push(visitorArgs[index].context.staticData);
        }
        else {
            returnValue.push({});
        }
    });
    return returnValue;
};
export const buildComparisonPipeline = (config) => (dataList, request) => {
    const pipelineArgs = dataList || [];
    const returnValue = [];
    const visitorArgs = [];
    pipelineArgs.forEach((data, index) => {
        const innerPipeline = buildPipeline(config);
        const root = (data.entities || []).map((entity) => innerPipeline([entity], request).map((comparate) => {
            const { root: items, ...rest } = comparate;
            return {
                entityAlias: entity.id ?? "",
                items: items ?? [],
                ...rest
            };
        })).flat();
        if (config?.comparisonVisitorsReducer) {
            visitorArgs.push({
                context: {
                    componentProps: config.componentProps,
                    request,
                    staticData: { dimensions: [], root },
                    visitNodePassage: VisitNodePassage.Always,
                    visitedDimensions: {},
                    widgetState: config.widgetState
                },
                visitable: {}
            });
            buildComparisonVisitor(config.comparisonVisitorsReducer, config.customVisitorsReducer)(visitorArgs[index]);
            returnValue.push(visitorArgs[index].context.staticData);
        }
        else {
            returnValue.push({ root });
        }
    });
    return returnValue;
};
export const valueThis = (data, measures, dimensions) => {
    const config = {
        datapointsVisitorsReducer: [],
        nodesVisitorsReducer: []
    };
    let isTimeBased = false;
    const realDimensions = [];
    dimensions?.forEach((d) => {
        let dimensionName = "";
        let dimensionSort = null;
        if (typeof d === "string") {
            dimensionName = d;
        }
        else {
            ({ dimension: dimensionName, dimensionSort } = d);
        }
        isTimeBased = isTimeBased || dimensionName === ReferenceDateDimension;
        realDimensions.push(dimensionName);
        if (dimensionSort) {
            config.datapointsVisitorsReducer?.push({
                arguments: [dimensionName, dimensionSort?.toString()],
                method: DatapointsVisitorEnum.sortByDatapoint
            });
        }
        else if (dimensionName === ReferenceDateDimension) {
            config.datapointsVisitorsReducer?.push({
                arguments: [dimensionName, MeasureSeriesSortingType.Asc],
                method: DatapointsVisitorEnum.sortByDatapoint
            });
        }
    });
    if ((dimensions?.length ?? 0) === 0) {
        realDimensions.push(ReferenceDateDimension);
    }
    if (!isTimeBased) {
        config.datapointsVisitorsReducer?.push({
            method: DatapointsVisitorEnum.filterByMostRecentDate
        });
    }
    config.compositeObjectBuilder = {
        dimensions: realDimensions,
        measures,
        options: { flyWeight: true }
    };
    if ((dimensions?.length ?? 0) > 0) {
        config.nodesVisitorsReducer?.push({
            method: NodesVisitorEnum.getLeafValuesVisitor
        });
    }
    const composite = buildPipeline(config)([{ series: data }])[0].root ?? [];
    return composite.length ? composite[0].data || [] : [];
};
